// ============== PER-USER X / TWITTER OAUTH ==============
// Mirror of the LinkedIn card — lets ANY user connect their own X account so
// scheduled posts publish to THEIR feed instead of the admin account.
// X tokens (OAuth 1.0a) never expire — only revocation needs handling.
import {
  useState, useEffect, axios, Button, Card, CardContent, CardHeader, CardTitle,
  Badge, Loader2, API, toast,
} from "@/pages/shared";

export const TwitterPersonalSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/social-connections/twitter/status`);
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "twitter") {
      toast.success("X connected — scheduled posts will publish to your feed");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("twitter_error")) {
      toast.error(`X connection failed: ${params.get("twitter_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/social-connections/twitter/start`);
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start X connection");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm("Disconnect X? Scheduled posts will publish via the Creatara account instead.")) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API}/social-connections/twitter/disconnect`);
      toast.success("X disconnected");
      await fetchStatus();
    } catch (e) {
      toast.error("Couldn't disconnect");
    }
    setDisconnecting(false);
  };

  const connected = status?.connected;

  return (
    <Card className="glass" data-testid="settings-twitter-personal-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          Your X account
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Connect your personal X (Twitter) so scheduled posts publish directly to your feed. Without this, posts go out via the Creatara account.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : connected ? (
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5">
            <div className="flex items-center gap-3">
              {status.picture && (
                <img src={status.picture} alt="" className="w-10 h-10 rounded-full" data-testid="twitter-personal-avatar" />
              )}
              <div>
                <p className="font-medium" data-testid="twitter-personal-name">{status.name || "X user"}</p>
                <p className="text-xs text-muted-foreground" data-testid="twitter-personal-handle">@{status.screen_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-teal-500/20 text-teal-400" data-testid="twitter-personal-badge">✓ Connected</Badge>
              <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={disconnecting} data-testid="twitter-personal-disconnect-btn">
                {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disconnect"}
              </Button>
            </div>
          </div>
        ) : status?.revoked ? (
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center gap-3">
              {status.picture && (
                <img src={status.picture} alt="" className="w-10 h-10 rounded-full opacity-60" />
              )}
              <div>
                <p className="font-medium">{status.name || "X user"}</p>
                <p className="text-xs text-amber-400" data-testid="twitter-personal-revoked-msg">
                  Access revoked — scheduled posts can&apos;t publish to your feed
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-500/20 text-amber-400" data-testid="twitter-personal-revoked-badge">⚠ Reconnect needed</Badge>
              <Button onClick={handleConnect} disabled={connecting} data-testid="twitter-personal-reconnect-btn">
                {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reconnect"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5">
            <div>
              <p className="font-medium">X not connected</p>
              <p className="text-xs text-muted-foreground">Scheduled posts will publish via the Creatara account.</p>
            </div>
            <Button onClick={handleConnect} disabled={connecting} data-testid="twitter-personal-connect-btn">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect X"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
